import { darkColors } from '@/theme/colors';
import { ANNOTATION_STYLE } from '@/theme/constants';

/** Cornerstone per-annotation style overrides consumed by `setAnnotationStyles`. */
export interface AnnotationStyleConfig {
  color?: string;
  colorHighlighted?: string;
  colorSelected?: string;
  colorLocked?: string;
  lineWidth?: number | string;
  handleRadius?: number | string;
  showHandlesAlways?: boolean;
  textBoxVisibility?: boolean;
  textBoxColor?: string;
  textBoxColorHighlighted?: string;
  textBoxColorSelected?: string;
  textBoxColorLocked?: string;
  textBoxBackground?: string;
  textBoxBorderRadius?: number;
  textBoxMargin?: number;
  textBoxShadow?: boolean;
  textBoxLinkLineWidth?: number | string;
  textBoxLinkLineDash?: string;
  textBoxLinkLineColor?: string;
}

/**
 * Base style: green for AI, blue for human/manual, muted opacity, thin line,
 * small handles. Selection is driven by Redux (the caller passes
 * `overrides.isSelected`), not Cornerstone's internal selection — so the
 * inactive variant keeps every color slot muted.
 * Viewer canvas is always dark, so `darkColors` is the sole source.
 *
 * `annotation` is loosely typed because Cornerstone's `AnnotationData` doesn't
 * declare our custom `isAI` flag — we stash it on the data blob at hydration.
 */
export function getBaseAnnotationStyle(
  annotation: { data?: unknown },
  overrides: { isHovered?: boolean; isSelected?: boolean } = {}
): AnnotationStyleConfig {
  const data = annotation.data as { isAI?: boolean } | undefined;
  const isAI = !!data?.isAI;
  const inactive = isAI
    ? darkColors.annotationAiInactive
    : darkColors.annotationManualInactive;
  const active = isAI
    ? darkColors.annotationAiActive
    : darkColors.annotationManualActive;

  // Selected: full-opacity source color, thicker line. Endpoint handles are
  // NOT driven from here — Cornerstone's LengthTool reads `showHandlesAlways`
  // with an empty style specifier, so per-annotation overrides are ignored.
  // The selected line's handles are drawn by a render augmentation on the
  // LengthTool instance (see useSharedToolState). This `color` still feeds
  // those handles, so they render bright.
  // The on-image chip appears on hover, like any other annotation; the
  // label/value also renders in the pane header (SelectedAnnotationLabel).
  if (overrides.isSelected) {
    return {
      color: active,
      colorHighlighted: active,
      colorSelected: active,
      // Locked (finalized report) keeps the source colour, not Cornerstone's yellow.
      colorLocked: active,
      lineWidth: ANNOTATION_STYLE.lineWidthActive,
      handleRadius: ANNOTATION_STYLE.handleRadiusActive,
      textBoxVisibility: !!overrides.isHovered,
      textBoxColor: darkColors.annotationChipText,
      textBoxColorHighlighted: darkColors.annotationChipText,
      textBoxColorSelected: darkColors.annotationChipText,
      textBoxBackground: darkColors.annotationChipBg,
      textBoxBorderRadius: 6,
      textBoxMargin: 2,
      textBoxShadow: false,
      textBoxLinkLineColor: 'transparent',
      textBoxLinkLineDash: '',
    };
  }

  return {
    color: inactive,
    colorHighlighted: inactive,
    // Keep the inactive variant inactive even under Cornerstone's OWN selection
    // state. Selection is driven by Redux (the selection effect applies the
    // isSelected style above), so a click-to-deselect — which doesn't clear
    // Cornerstone's internal selection — must NOT leave the line bright. Using
    // `active` here made deselect inconsistent: muted when another annotation
    // was picked, bright when the same one was toggled off.
    colorSelected: inactive,
    colorLocked: inactive,
    lineWidth: ANNOTATION_STYLE.lineWidthInactive,
    handleRadius: ANNOTATION_STYLE.handleRadiusInactive,
    // Cornerstone always draws the textbox drag handle when a textbox exists
    // — including when getTextLines returns []. Flipping visibility to false
    // suppresses the entire textbox render. We toggle it back to true only
    // on the annotation currently being hovered (see the mousemove handler).
    textBoxVisibility: !!overrides.isHovered,
    textBoxColor: darkColors.annotationChipText,
    textBoxColorHighlighted: darkColors.annotationChipText,
    textBoxColorSelected: darkColors.annotationChipText,
    textBoxColorLocked: darkColors.annotationChipText,
    textBoxBackground: darkColors.annotationChipBg,
    textBoxBorderRadius: 6,
    // Small margin so the hover chip sits close to the cursor (the textBox is
    // anchored at the pointer; a large margin pushes it too far bottom-right).
    textBoxMargin: 2,
    // Disable Cornerstone's default text shadow — reads as a white halo
    // around the chip when the background is set.
    textBoxShadow: false,
    // Hide leader line via transparent stroke — Cornerstone falls back to
    // width=10 when lineWidth is 0 (falsy), so we can't hide it that way.
    textBoxLinkLineColor: 'transparent',
    textBoxLinkLineDash: '',
  };
}
